import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falBoxHeart } from 'shared/@generated/icons/falBoxHeart';
import { falBoxesStacked } from 'shared/@generated/icons/falBoxesStacked';
import { falTruckFast } from 'shared/@generated/icons/falTruckFast';
import { clsx } from 'clsx';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity, View } from 'react-native';
import { Product, ProductVariant } from 'shared/@generated/models';
import { Text } from 'shared/components/text';
import { Icon } from 'shared/components/icon';
import { Gallery } from 'shared/components/gallery';
import { Breadcrumb } from 'shared/components/breadcrumb';
import { PageBuilder } from 'shared/library/page-builder';
import { useRoute } from 'shared/library/routes/route';
import { apiRoute } from 'shared/library/api-route';
import { useFormatter } from 'shared/library/use-formatter';
import { H1, H2 } from 'shared/components/title';
import { PaymentBrands } from 'shared/components/payment-brands';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import LikeButton from 'shared/components/like-button';
import { ShopProductCard } from 'shared/components/shop/product-card';
import { ShopProductRating } from 'shared/components/shop/product-rating';
import { ShopProductReviews } from 'shared/components/shop/product-reviews';
import { AddToCartButton } from 'shared/components/shop/add-to-cart-button';
import useShopStore from 'shared/state/shop';
import { Button } from 'shared/components/button';
import { MarkdownView } from 'shared/components/markdown-view';
import { Image } from 'shared/components/image';
import { Link } from 'shared/components/link';
import { getProductGalleryAssets, getProductImage } from 'shared/library/get-model-image';

export interface ShopProductScreenProps {
  product: Product;
  similarProducts: Product[];
}

function ShopProductScreen({ product, similarProducts }: ShopProductScreenProps) {
  const { t } = useTranslation('screens');
  const { formatCurrency } = useFormatter();
  const route = useRoute();
  const order = useShopStore((state) => state.order);
  const settings = useShopStore((state) => state.settings);
  const productVariants = product.product_variants ?? [];
  const productCategories = product.product_categories ?? [];
  const productGalleryAssets = useMemo(() => getProductGalleryAssets(product), [product]);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    productVariants[0] ?? null,
  );

  const variantsWithNames = useMemo(() => {
    return productVariants.filter((variant) => variant.name);
  }, [productVariants]);

  const selectedRecommendedSavings = useMemo(() => {
    if (!selectedVariant) return null;

    const savings =
      selectedVariant.recommended_pricing.local.total.amount -
      selectedVariant.pricing.local.total.amount;

    return savings > 0 ? savings : null;
  }, [selectedVariant]);

  return (
    <View className={'p-3 md:p-6'}>
      <View className={'mx-auto w-full max-w-screen-xl'}>
        <View className={'grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-6'}>
          <View>
            <Gallery
              items={productGalleryAssets}
              defaultAlt={product.name}
              aspect={'square'}
              overlay={<LikeButton modelId={product.id} modelType={'App\\Models\\Product'} />}
            />
          </View>

          <View className={'md:pt-2'}>
            {Platform.OS === 'web' && (
              <Breadcrumb
                className={'mb-1.5'}
                items={[
                  {
                    text: t('shop.shop_home.title'),
                    href: route('shop.home'),
                  },
                  {
                    text: productCategories[0]?.name ?? '',
                    href: route('shop.category', {
                      category: productCategories[0]?.slug ?? '',
                    }),
                  },
                  {
                    text: product.brand.name,
                  },
                ]}
              />
            )}

            <H1 title={product.name} marginBottom={false} hideTitleWhenNativeHeader={true} />

            <ShopProductRating product={product} className={'mt-3'} />

            {product.summary && (
              <Text className={'text-brand-brown-dark mt-3 max-w-prose text-pretty md:text-lg'}>
                {product.summary}
              </Text>
            )}

            {variantsWithNames.length > 0 && (
              <View className={'mb-6 mt-6 flex-row flex-wrap gap-1.5'}>
                {variantsWithNames.map((productVariant) => {
                  const isSelected = selectedVariant?.id === productVariant.id;

                  return (
                    <TouchableOpacity
                      key={productVariant.id}
                      className={`rounded-full border px-3 py-1.5 ${
                        isSelected
                          ? 'border-brand-brown-dark bg-brand-brown-dark'
                          : 'border-brand-brown-dark/20 bg-white'
                      } ${productVariant.available ? '' : 'opacity-60'}`}
                      onPress={() => setSelectedVariant(productVariant)}
                      disabled={!productVariant.available}
                    >
                      <Text
                        className={`text-sm font-bold ${
                          isSelected ? 'text-brand-cream' : 'text-brand-brown-dark'
                        }`}
                      >
                        {productVariant.name}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}

            <View
              className={clsx({
                'mt-5 md:mt-6': variantsWithNames.length === 0,
              })}
            >
              <Text className={'text-brand-brown-dark text-2xl font-black md:text-3xl'}>
                {selectedVariant
                  ? formatCurrency(selectedVariant.pricing.local.total.amount, undefined, 2)
                  : '-'}
              </Text>

              {selectedRecommendedSavings && (
                <View className={'md:mt-1.5'}>
                  <Text className={'font-bold text-red-700 md:text-lg'}>
                    {t('shop.shop_product.savings', {
                      value: formatCurrency(selectedRecommendedSavings, 'kr', 2),
                    })}
                  </Text>

                  <Text className={'text-brand-brown-dark/70'}>
                    {t('shop.shop_product.recommended_price', {
                      value: selectedVariant
                        ? formatCurrency(
                            selectedVariant.recommended_pricing.local.total.amount,
                            undefined,
                            2,
                          )
                        : '-',
                    })}
                  </Text>
                </View>
              )}

              {selectedVariant && (
                <View className={'mt-3 md:mt-6'}>
                  <AddToCartButton product={product} variant={selectedVariant} />
                </View>
              )}

              {order && (order.order_items?.length ?? 0) > 0 && (
                <Button
                  className={'mt-3'}
                  text={t('shop.shop_product.go_to_checkout')}
                  icon={falAngleRight}
                  href={route('shop.checkout')}
                  color={'brown'}
                  center={true}
                />
              )}

              <View className={'mt-3 gap-3 md:mt-6 md:flex-row md:flex-wrap md:justify-between'}>
                <View className={'flex-row items-center gap-3'}>
                  <Icon icon={falBoxesStacked} className={'text-brand-brown-dark'} />
                  <Text className={'text-brand-brown-dark'}>
                    {selectedVariant && selectedVariant.in_stock > 0
                      ? t('shop.shop_product.in_stock')
                      : t('shop.shop_product.out_of_stock')}
                  </Text>
                  <View
                    className={
                      'border-brand-brown-dark/15 -ml-1.5 size-3.5 rounded-full border bg-white p-0.5'
                    }
                  >
                    <View
                      className={`size-full rounded-full ${
                        selectedVariant && selectedVariant.in_stock > 0
                          ? 'bg-brand-green'
                          : 'bg-orange-500'
                      }`}
                    />
                  </View>
                </View>

                <View className={'flex-row items-center gap-3'}>
                  <Icon icon={falTruckFast} className={'text-brand-brown-dark'} />
                  <Text className={'text-brand-brown-dark'}>
                    {t('shop.shop_product.delivery_time', {
                      deliveryMin:
                        (selectedVariant?.in_stock ?? 0) > 0
                          ? settings?.shipping_days_min
                          : settings?.shipping_days_slow_min,
                      deliveryMax:
                        (selectedVariant?.in_stock ?? 0) > 0
                          ? settings?.shipping_days_max
                          : settings?.shipping_days_slow_max,
                    })}
                  </Text>
                </View>

                <View className={'flex-row items-center gap-3'}>
                  <Icon icon={falBoxHeart} className={'text-brand-brown-dark'} />
                  <Text className={'text-brand-brown-dark'}>
                    {t('shop.shop_product.delivery_free_shipping', {
                      freeShippingFrom: (settings?.free_shipping_from_minor ?? 0) / 100,
                    })}
                  </Text>
                </View>
              </View>

              <ReviewsSummaryCard
                className={'border-brand-brown-dark/15 mt-3 justify-center border bg-white md:mt-6'}
                horizontal={true}
              />

              <View className={'mt-3 items-center md:mt-6'}>
                <PaymentBrands />
              </View>
            </View>
          </View>
        </View>

        {selectedVariant && (selectedVariant.bundle_variants?.length ?? 0) > 0 && (
          <>
            <H2 title={t('shop.shop_product.products_in_bundle')} />

            <View
              className={clsx(
                'gap-3 md:gap-6',
                Platform.OS === 'web' && 'flex-cols-1 md:flex-cols-3 flex-row',
              )}
            >
              {[...(selectedVariant.bundle_variants ?? [])]
                .sort((a, b) => b.pricing.local.total.amount - a.pricing.local.total.amount)
                .map((item) => (
                  <Link key={item.id} href={route('shop.product', { product: item.product.slug })}>
                    <View className={'flex-row items-center'}>
                      <Image
                        src={getProductImage(item.product)}
                        alt={item.product.name}
                        sizeType={'card'}
                        containerClassName={'size-28'}
                        imageClassName={
                          'border-brand-brown-dark/15 rounded border bg-white object-cover group-hover:opacity-75'
                        }
                        aspectRatio={1}
                      />

                      <View className={'flex-1 p-3'}>
                        <Text
                          className={
                            'text-brand-brown-dark/70 text-2xs uppercase tracking-wider group-hover:underline md:text-xs'
                          }
                        >
                          {item.product.brand.name}
                        </Text>
                        <Text
                          className={'text-brand-brown-dark font-semibold group-hover:underline'}
                        >
                          {item.product.name}
                        </Text>
                        <Text className={'text-brand-brown-dark mt-3 font-black'}>
                          {formatCurrency(item.pricing.local.total.amount, undefined, 2)}
                        </Text>
                      </View>
                    </View>
                  </Link>
                ))}
            </View>
          </>
        )}

        {product.description && product.description.length > 0 && (
          <>
            <H2 title={t('shop.shop_product.product_details')} />

            <MarkdownView
              className={'text-brand-brown-dark/70 max-w-none text-base'}
              markdown={product.description}
            />
          </>
        )}

        <ShopProductReviews product={product} />

        <H2 title={t('shop.shop_product.similar_products')} />

        <View
          className={clsx(
            'gap-3 md:gap-6',
            Platform.OS === 'web' && 'flex-cols-1 md:flex-cols-4 flex-row',
          )}
        >
          {similarProducts.map((similarProduct) => (
            <View key={similarProduct.id}>
              <ShopProductCard product={similarProduct} />
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

ShopProductScreen.pageBuilder = new PageBuilder<ShopProductScreenProps, { product: string }>()
  .includeShopData()
  .request(({ req, params }) =>
    req<{ product: Product; similar_products: Product[] }>(
      apiRoute('products.show', { product: params.product }),
    ).then((response) => ({
      product: response.product,
      similarProducts: response.similar_products,
    })),
  );

export { ShopProductScreen };
